<template>
  <div class="overview-container">
    <div class="overview-header">
      <div class="header-content">
        <button @click.stop="goBack" class="back-button">
          <span class="back-icon">←</span>
          <span>返回</span>
        </button>
        <div class="header-title">
          <div class="title-icon">📊</div>
          <h1>数据概览</h1>
        </div>
      </div>
    </div>

    <div class="overview-content">
      <div class="overview-toolbar">
        <div class="toolbar-left">
          <span class="toolbar-title">自定义指标</span>
          <span class="toolbar-count">共 {{ sortedJobList.length }} 条</span>
        </div>
        <div class="toolbar-right">
          <button class="action-button start" type="button" :disabled="batchActionLoading || loading" @click="handleBatchBoost(true)">
            一键起量
          </button>
          <button class="action-button stop" type="button" :disabled="batchActionLoading || loading" @click="handleBatchBoost(false)">
            一键停止
          </button>
          <label class="toolbar-label">
            投放状态
            <select v-model="selectedStatus" class="toolbar-select">
              <option value="">全部状态</option>
              <option v-for="option in statusFilterOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="toolbar-label">
            排序字段
            <select v-model="sortField" class="toolbar-select">
              <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="toolbar-label">
            顺序
            <select v-model="sortOrder" class="toolbar-select">
              <option value="desc">从高到低</option>
              <option value="asc">从低到高</option>
            </select>
          </label>
          <button class="refresh-button" type="button" :disabled="loading" @click="fetchJobList">
            {{ loading ? '加载中...' : '刷新' }}
          </button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-item">
          <div class="summary-label">汇总整体消耗</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.totalStatCost) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">汇总净成交订单数</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.totalOrderCount, 0) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">汇总净成交金额</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.totalOrderAmount) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">汇总净成交ROI</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.avgSettleRoi) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">汇总1小时内退款率</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.avgRefundRate) }}%</div>
        </div>
      </div>

      <div v-if="errorMessage" class="status-message error">{{ errorMessage }}</div>
      <div v-if="actionMessage" class="status-message action">{{ actionMessage }}</div>
      <div v-if="!loading && sortedJobList.length === 0" class="status-message empty">暂无数据</div>

      <div class="job-card-list">
        <div v-for="item in sortedJobList" :key="item.id || item.jobId" class="job-card">
          <div class="job-card-header">
            <div class="job-avatar">🏳️</div>
            <div class="job-base">
              <div class="target-name">{{ item.targetName || '-' }}</div>
              <div class="target-id">ID：{{ item.targetId || '-' }}</div>
              <div class="job-meta">任务：{{ item.jobName || '-' }} ｜ 投放状态：{{ getStatusText(item.status) }}</div>
              <div class="material-threshold-row">
                <label class="material-threshold-label">视频限额</label>
                <input
                  class="material-threshold-input"
                  type="number"
                  min="0"
                  :value="getMaterialThresholdInput(item)"
                  @input="setMaterialThresholdInput(item, $event.target.value)"
                />
                <button
                  class="material-threshold-save"
                  type="button"
                  :disabled="isMaterialThresholdSaving(item)"
                  @click="saveMaterialThreshold(item)"
                >
                  {{ isMaterialThresholdSaving(item) ? '保存中...' : '保存' }}
                </button>
              </div>
            </div>
          </div>

          <div class="metrics-grid primary-grid">
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.statCost) }}</div>
              <div class="metric-label">整体消耗</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.totalPayOrderGmvForRoi2) }}</div>
              <div class="metric-label">用户实际支付金额</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.totalPrepayAndPaySettleRoi21h) }}</div>
              <div class="metric-label">净成交ROI</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.totalOrderSettleAmountForRoi21h) }}</div>
              <div class="metric-label">净成交金额</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.totalOrderSettleCountForRoi21h, 0) }}</div>
              <div class="metric-label">净成交订单数</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.targetBlance) }}</div>
              <div class="metric-label">账户余额</div>
            </div>
          </div>

          <div class="metrics-grid secondary-grid">
            <div class="metric-item secondary">
              <div class="metric-value secondary-value">{{ formatNumber(item.totalCostPerPayOrderSettleForRoi21h) }}</div>
              <div class="metric-label">净成交订单成本</div>
            </div>
            <div class="metric-item secondary">
              <div class="metric-value secondary-value">{{ formatPercent(item.totalRefundOrderGmvForRoi21hRate) }}</div>
              <div class="metric-label">1小时内退款率</div>
            </div>
            <div class="metric-item secondary">
              <div class="metric-value secondary-value">{{ formatNumber(item.totalRefundOrderCountForRoi21h, 0) }}</div>
              <div class="metric-label">1小时内退款订单数</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'

const STATUS_MAP = {
  ADVERTISER_OFFLINE_BUDGET: '账户超出预算',
  ADVERTISER_PRE_OFFLINE_BUDGET: '账户接近预算',
  AUDIT: '新建审核中',
  AUDIT_STATUS_ERROR: '异常，请联系审核人员',
  AWEME_ACCOUNT_DISABLED: '关联抖音号不可投',
  CAMPAIGN_DISABLE: '已被计划组暂停',
  CAMPAIGN_OFFLINE_BUDGET: '计划组超出预算',
  CAMPAIGN_PRE_OFFLINE_BUDGET: '计划组接近预算',
  CREATE: '计划新建',
  DELETE: '已删除',
  DELIVERY_OK: '投放中',
  DISABLE: '已暂停',
  DRAFT: '草稿',
  ERROR: '数据错误',
  EXTERNAL_URL_DISABLE: '落地页暂不可用',
  FROZEN: '已终止',
  LIVE_ROOM_OFF: '关联直播间未开播',
  NO_SCHEDULE: '不在投放时段',
  OFFLINE_AUDIT: '审核不通过',
  OFFLINE_BALANCE: '账户余额不足',
  OFFLINE_BUDGET: '营销超出预算',
  PRE_OFFLINE_BUDGET: '营销接近预算',
  PRE_ONLINE: '预上线',
  QUOTA_DISABLE: 'QUOTA配额不足暂停计划',
  REAUDIT: '修改审核中',
  SYSTEM_DISABLE: '系统暂停',
  TIME_DONE: '已完成',
  TIME_NO_REACH: '未到达投放时间'
}

export default {
  name: 'DataOverview',
  data() {
    return {
      jobList: [],
      loading: false,
      listRequesting: false,
      autoRefreshTimer: null,
      cardNumber: '',
      errorMessage: '',
      selectedStatus: 'DELIVERY_OK',
      actionMessage: '',
      batchActionLoading: false,
      materialThresholdInputs: {},
      materialThresholdSavingMap: {},
      boostBudget: '100',
      boostDuration: '24',
      sortField: 'statCost',
      sortOrder: 'desc',
      sortOptions: [
        { label: '整体消耗', value: 'statCost', type: 'number' },
        { label: '用户实际支付金额', value: 'totalPayOrderGmvForRoi2', type: 'number' },
        { label: '净成交ROI', value: 'totalPrepayAndPaySettleRoi21h', type: 'number' },
        { label: '净成交金额', value: 'totalOrderSettleAmountForRoi21h', type: 'number' },
        { label: '净成交订单数', value: 'totalOrderSettleCountForRoi21h', type: 'number' },
        { label: '账户余额', value: 'targetBlance', type: 'number' },
        { label: '1小时内退款率', value: 'totalRefundOrderGmvForRoi21hRate', type: 'number' },
        { label: '锚定账户名称', value: 'targetName', type: 'string' }
      ]
    }
  },
  computed: {
    statusFilterOptions() {
      return Object.keys(STATUS_MAP).map(code => ({
        value: code,
        label: STATUS_MAP[code]
      }))
    },
    filteredJobList() {
      if (!this.selectedStatus) {
        return this.jobList
      }
      return this.jobList.filter(item => item.status === this.selectedStatus)
    },
    sortedJobList() {
      const list = [...this.filteredJobList]
      const selectedOption = this.sortOptions.find(item => item.value === this.sortField)
      if (!selectedOption) {
        return list
      }
      const direction = this.sortOrder === 'asc' ? 1 : -1
      return list.sort((a, b) => {
        const aValue = a[this.sortField]
        const bValue = b[this.sortField]
        if (selectedOption.type === 'string') {
          const aString = String(aValue || '')
          const bString = String(bValue || '')
          return aString.localeCompare(bString) * direction
        }
        const aNumber = this.toNumber(aValue)
        const bNumber = this.toNumber(bValue)
        return (aNumber - bNumber) * direction
      })
    },
    summaryMetrics() {
      const totalStatCost = this.sortedJobList.reduce((sum, item) => sum + this.toNumber(item.statCost), 0)
      const totalOrderCount = this.sortedJobList.reduce((sum, item) => sum + this.toNumber(item.totalOrderSettleCountForRoi21h), 0)
      const totalOrderAmount = this.sortedJobList.reduce((sum, item) => sum + this.toNumber(item.totalOrderSettleAmountForRoi21h), 0)
      const totalRefundOrderCount = this.sortedJobList.reduce((sum, item) => sum + this.toNumber(item.totalRefundOrderCountForRoi21h), 0)
      const avgSettleRoi = totalStatCost > 0 ? totalOrderAmount / totalStatCost : 0
      const totalOrderBase = totalRefundOrderCount + totalOrderCount
      const avgRefundRate = totalOrderBase > 0 ? (totalRefundOrderCount / totalOrderBase) * 100 : 0
      return {
        totalStatCost,
        totalOrderCount,
        totalOrderAmount,
        avgSettleRoi,
        avgRefundRate
      }
    }
  },
  created() {
    this.cardNumber = this.$route.query.cardNumber || localStorage.getItem('cardNumber') || ''
    this.fetchJobList()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  methods: {
    toSafeHeaderValue(value) {
      const str = String(value ?? '')
      for (let i = 0; i < str.length; i += 1) {
        if (str.charCodeAt(i) > 255) {
          return encodeURIComponent(str)
        }
      }
      return str
    },
    goBack() {
      this.$router.push('/home')
    },
    toNumber(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    formatNumber(value, fractionDigits = 2) {
      const numberValue = this.toNumber(value)
      return numberValue.toLocaleString('zh-CN', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      })
    },
    formatPercent(value) {
      const numberValue = this.toNumber(value)
      if (numberValue <= 1) {
        return `${(numberValue * 100).toFixed(2)}%`
      }
      return `${numberValue.toFixed(2)}%`
    },
    getStatusText(status) {
      return STATUS_MAP[status] || status || '-'
    },
    normalizeJobList(list) {
      return (list || []).map(item => ({
        id: item.id,
        username: item.username ?? item.userName ?? item.user_name ?? '',
        advertiserId: item.advertiserId ?? item.advertiser_id ?? item.targetId ?? item.target_id,
        targetId: item.targetId ?? item.target_id,
        targetBlance: item.targetBlance ?? item.target_blance ?? item.targetBalance ?? item.target_balance,
        targetName: item.targetName ?? item.target_name,
        jobId: item.jobId ?? item.job_id,
        jobName: item.jobName ?? item.job_name,
        roi2Goal: item.roi2Goal ?? item.roi2_goal,
        status: item.status,
        statCost: item.statCost ?? item.stat_cost,
        totalPrepayAndPayOrderRoi2: item.totalPrepayAndPayOrderRoi2 ?? item.total_prepay_and_pay_order_roi2,
        totalPayOrderGmvForRoi2: item.totalPayOrderGmvForRoi2 ?? item.total_pay_order_gmv_for_roi2,
        totalCostPerPayOrderSettleForRoi21h: item.totalCostPerPayOrderSettleForRoi21h ?? item.total_cost_per_pay_order_settle_for_roi21h,
        totalOrderSettleCountForRoi21h: item.totalOrderSettleCountForRoi21h ?? item.total_order_settle_count_for_roi21h,
        totalOrderSettleAmountForRoi21h: item.totalOrderSettleAmountForRoi21h ?? item.total_order_settle_amount_for_roi21h,
        totalPrepayAndPaySettleRoi21h: item.totalPrepayAndPaySettleRoi21h ?? item.total_prepay_and_pay_settle_roi21h,
        totalRefundOrderGmvForRoi21hRate: item.totalRefundOrderGmvForRoi21hRate ?? item.total_refund_order_gmv_for_roi21h_rate,
        totalRefundOrderCountForRoi21h: item.totalRefundOrderCountForRoi21h ?? item.total_refund_order_count_for_roi21h,
        materialThreshold: item.materialThreshold ?? item.material_threshold ?? item.limitValue ?? item.limit_value
      }))
    },
    getJobAdId(item) {
      return item?.jobId ?? item?.job_id ?? ''
    },
    getJobAdvertiserId(item) {
      return item?.advertiserId ?? item?.targetId ?? ''
    },
    getJobUsername(item) {
      return String(item?.username ?? item?.userName ?? item?.user_name ?? '').trim()
    },
    getItemActionKey(item) {
      return `${this.getJobAdvertiserId(item)}_${this.getJobAdId(item)}`
    },
    getMaterialThresholdKey(item) {
      return String(this.getJobAdId(item) || item?.id || '')
    },
    getMaterialThresholdInput(item) {
      const key = this.getMaterialThresholdKey(item)
      if (!key) {
        return ''
      }
      if (Object.prototype.hasOwnProperty.call(this.materialThresholdInputs, key)) {
        return this.materialThresholdInputs[key]
      }
      return item?.materialThreshold !== undefined && item?.materialThreshold !== null
        ? String(item.materialThreshold)
        : ''
    },
    setMaterialThresholdInput(item, value) {
      const key = this.getMaterialThresholdKey(item)
      if (!key) {
        return
      }
      this.$set(this.materialThresholdInputs, key, value)
    },
    isMaterialThresholdSaving(item) {
      const key = this.getMaterialThresholdKey(item)
      return !!this.materialThresholdSavingMap[key]
    },
    parseNonNegativeNumber(value) {
      const num = Number(value)
      if (!Number.isFinite(num) || num < 0) {
        return null
      }
      return num
    },
    async saveMaterialThreshold(item) {
      const key = this.getMaterialThresholdKey(item)
      const jobId = this.getJobAdId(item)
      const requestUsername = this.getJobUsername(item)
      if (!key || !jobId) {
        this.actionMessage = '缺少 jobId，无法保存视频限额'
        return
      }
      const rawValue = this.getMaterialThresholdInput(item)
      const limitValue = this.parseNonNegativeNumber(rawValue)
      if (limitValue === null) {
        this.actionMessage = '视频限额需为大于等于0的数字'
        return
      }
      this.$set(this.materialThresholdSavingMap, key, true)
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const username = this.toSafeHeaderValue(requestUsername || '')
        const headers = {
          'Content-Type': 'application/json'
        }
        if (token) {
          headers.Authorization = `Bearer ${safeToken}`
        }
        if (username) {
          headers.username = username
        }
        const body = {
          jobId: Number(jobId),
          limitValue
        }
        const response = await axios.post(API_CONFIG.jobMaterialThreshold(), body, { headers })
        const data = response?.data || {}
        if (Number(data.code) === 200) {
          this.$set(this.materialThresholdInputs, key, String(limitValue))
          item.materialThreshold = limitValue
          this.actionMessage = `${item.targetName || jobId} 视频限额保存成功`
        } else {
          this.actionMessage = data.msg || data.message || '视频限额保存失败'
        }
      } catch (error) {
        this.actionMessage = error.response?.data?.msg || error.response?.data?.message || '视频限额保存失败'
      } finally {
        this.$set(this.materialThresholdSavingMap, key, false)
      }
    },
    parsePositiveInt(value) {
      const num = Number(value)
      if (!Number.isFinite(num) || num <= 0) {
        return 0
      }
      return Math.floor(num)
    },
    buildSmartBoostPayload(item, enable, customBudget, customDuration) {
      const advertiserId = this.getJobAdvertiserId(item)
      const adId = this.getJobAdId(item)
      if (!advertiserId || !adId) {
        return null
      }
      const payload = {
        advertiser_id: String(advertiserId),
        ad_id: String(adId),
        enable: !!enable
      }
      if (enable) {
        const budget = this.parsePositiveInt(customBudget != null ? customBudget : this.boostBudget)
        const duration = this.parsePositiveInt(customDuration != null ? customDuration : this.boostDuration)
        if (!budget || !duration) {
          return null
        }
        payload.budget = budget
        payload.duration = duration
      }
      return payload
    },
    async requestSmartBoost(payload, requestUsername) {
      const token = localStorage.getItem('authToken')
      const accessToken = localStorage.getItem('accessToken') || token || ''
      const safeToken = this.toSafeHeaderValue(token)
      const username = this.toSafeHeaderValue(requestUsername || '')
      const headers = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers.Authorization = `Bearer ${safeToken}`
      }
      if (username) {
        headers.username = username
      }
      const requestBody = {
        ...payload,
        accessToken: String(accessToken || '')
      }
      const response = await axios.post(API_CONFIG.smartBoostSwitch(), requestBody, { headers })
      return response?.data || {}
    },
    async handleBatchBoost(enable) {
      if (this.batchActionLoading) {
        return
      }
      const targetList = this.jobList || []
      if (targetList.length === 0) {
        this.actionMessage = '暂无可操作数据'
        return
      }
      if (enable) {
        const budget = this.parsePositiveInt(this.boostBudget)
        const duration = this.parsePositiveInt(this.boostDuration)
        if (!budget || !duration) {
          this.actionMessage = '一键起量前请先填写预算和时长（正整数）'
          return
        }
      }
      this.batchActionLoading = true
      this.actionMessage = ''
      let successCount = 0
      let failCount = 0
      for (const item of targetList) {
        const payload = this.buildSmartBoostPayload(item, enable)
        const requestUsername = this.getJobUsername(item)
        if (!payload) {
          failCount += 1
          continue
        }
        if (!requestUsername) {
          failCount += 1
          continue
        }
        try {
          const data = await this.requestSmartBoost(payload, requestUsername)
          if (Number(data.code) === 200) {
            successCount += 1
          } else {
            failCount += 1
          }
        } catch (error) {
          failCount += 1
        }
      }
      this.batchActionLoading = false
      this.actionMessage = `一键${enable ? '起量' : '停止'}完成（全量任务）：成功 ${successCount} 条，失败 ${failCount} 条`
      this.fetchJobList({ silent: true })
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.autoRefreshTimer = setInterval(() => {
        this.fetchJobList({ silent: true })
      }, 20000)
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },
    async fetchJobList(options = {}) {
      const { silent = false } = options
      if (this.listRequesting) {
        return
      }
      this.listRequesting = true
      if (!silent) {
        this.loading = true
        this.errorMessage = ''
      }
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const response = await axios.get(API_CONFIG.jobList(), {
          params: this.cardNumber ? { cardNumber: this.cardNumber } : {},
          headers: token
            ? {
                Authorization: `Bearer ${safeToken}`
              }
            : {}
        })
        if (response.data && response.data.code === 200) {
          const rows = response.data.rows || response.data.data || []
          this.jobList = this.normalizeJobList(rows)
          const nextInputs = {}
          this.jobList.forEach((item) => {
            const key = this.getMaterialThresholdKey(item)
            if (!key) {
              return
            }
            const value = item.materialThreshold
            nextInputs[key] = value !== undefined && value !== null ? String(value) : ''
          })
          this.materialThresholdInputs = nextInputs
        } else {
          if (!silent) {
            this.errorMessage = (response.data && (response.data.msg || response.data.message)) || '获取数据概览失败'
            this.jobList = []
          }
        }
      } catch (error) {
        if (!silent) {
          this.errorMessage = error.response?.data?.msg || error.response?.data?.message || '获取数据概览失败'
          this.jobList = []
        }
      } finally {
        this.listRequesting = false
        if (!silent) {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.overview-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
}

.overview-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px 0;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  padding: 10px 20px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
}

.header-title h1 {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.overview-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.overview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-title {
  color: rgba(255, 255, 255, 0.92);
  font-size: 18px;
  font-weight: 600;
}

.toolbar-count {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
}

.toolbar-select {
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 6px 8px;
  min-width: 130px;
}

.toolbar-select option {
  color: #1f2a44;
}

.action-button {
  border: none;
  border-radius: 999px;
  color: #fff;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
}

.action-button.start {
  background: linear-gradient(45deg, #2ecc71, #27ae60);
}

.action-button.stop {
  background: linear-gradient(45deg, #ff6b6b, #ee5253);
}

.action-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refresh-button {
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 8px 14px;
  cursor: pointer;
}

.refresh-button:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.status-message {
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 12px;
}

.status-message.error {
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.4);
  color: #ff9f9f;
}

.status-message.empty {
  background: rgba(255, 255, 255, 0.1);
  border: 1px dashed rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.82);
  text-align: center;
}

.status-message.action {
  background: rgba(46, 204, 113, 0.2);
  border: 1px solid rgba(46, 204, 113, 0.45);
  color: #c8f7dc;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.summary-item {
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  padding: 10px 12px;
}

.summary-label {
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
}

.summary-value {
  margin-top: 4px;
  color: #ffffff;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
}

.job-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.job-card {
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  padding: 14px 16px;
}

.job-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.job-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.88);
  color: #3a4d71;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.job-base {
  min-width: 0;
  flex: 1;
}

.target-name {
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.15;
  word-break: break-all;
}

.target-id {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 18px;
  line-height: 1.2;
}

.job-meta {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
}

.material-threshold-row {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.material-threshold-label {
  color: rgba(255, 255, 255, 0.86);
  font-size: 13px;
}

.material-threshold-input {
  width: 110px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 6px 8px;
  font-size: 13px;
}

.material-threshold-save {
  border: none;
  border-radius: 8px;
  padding: 6px 10px;
  color: #fff;
  background: linear-gradient(45deg, #667eea, #5b6ce1);
  cursor: pointer;
  font-size: 12px;
}

.material-threshold-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.metrics-grid {
  display: grid;
  gap: 10px;
}

.primary-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.secondary-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 6px;
}

.metric-item {
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  padding: 10px 12px;
}

.metric-item.secondary {
  background: rgba(255, 255, 255, 0.04);
}

.metric-value {
  color: #ffffff;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 800;
}

.secondary-value {
  font-size: 24px;
}

.metric-label {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 16px;
  line-height: 1.2;
}

@media (max-width: 992px) {
  .overview-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-left {
    justify-content: space-between;
  }

  .toolbar-right {
    justify-content: space-between;
  }

  .primary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .secondary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .target-name {
    font-size: 24px;
  }

  .metric-value {
    font-size: 24px;
  }

  .metric-label {
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .overview-content {
    padding: 12px 10px 18px;
  }

  .header-content {
    padding: 0 12px;
  }

  .back-button {
    padding: 8px 14px;
    font-size: 13px;
  }

  .header-title h1 {
    font-size: 20px;
  }

  .overview-toolbar {
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    padding: 10px;
    margin-bottom: 10px;
  }

  .toolbar-left,
  .toolbar-right {
    width: 100%;
  }

  .toolbar-right {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .toolbar-label {
    justify-content: space-between;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
    padding: 8px 10px;
  }

  .toolbar-select {
    min-width: 150px;
  }

  .refresh-button {
    width: 100%;
    border-radius: 10px;
    text-align: center;
  }

  .action-button {
    width: 100%;
    border-radius: 10px;
  }

  .primary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 10px;
  }

  .secondary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .summary-item {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.14);
    padding: 10px;
  }

  .summary-value {
    font-size: 20px;
  }

  .job-card-list {
    gap: 10px;
  }

  .job-card {
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.26);
    background: rgba(255, 255, 255, 0.12);
    padding: 12px;
  }

  .job-card-header {
    margin-bottom: 10px;
  }

  .target-name {
    font-size: 20px;
  }

  .target-id {
    font-size: 14px;
  }

  .job-meta {
    font-size: 12px;
  }

  .material-threshold-row {
    flex-wrap: wrap;
  }

  .metric-item {
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.11);
    padding: 10px;
  }

  .metric-value {
    font-size: 20px;
  }

  .secondary-value {
    font-size: 18px;
  }

  .metric-label {
    font-size: 13px;
  }
}
</style>
